import styled, { css } from 'styled-components';

import { Link } from 'gatsby';

import { ButtonAppearance, ButtonTheme } from './Button.js';

import { Color, Gradient } from '../../../shared/constants/Colors';
import {
  FontSize,
  FontType,
  FontWeight,
} from '../../../shared/constants/Fonts';
import {
  MediaQuery,
  Shadow,
  Transition,
} from '../../../shared/constants/Styles';

const ThemeButton = css`
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'initial')};
  transition: ${Transition.ALL_EASE};
  min-height: 45px;
  border-radius: 22.5px;
  background-clip: padding-box;
  outline: 0;
  display: inline-flex;
  align-items: center;
  font-family: ${FontType.PRIMARY};
  color: ${({ isDisabled }) => (isDisabled ? Color.DOLPHIN : Color.WHITE)};
  font-weight: ${FontWeight.BLACK};
  font-size: ${FontSize.S};
  letter-spacing: 0;
  line-height: 19px;
  box-sizing: border-box;
  flex-direction: ${({ isIconOnRight }) =>
    isIconOnRight ? 'row-reverse' : 'default'};

  svg {
    font-size: 15px;
  }

  ${({ appearance, isDisabled, isIconOnRight }) =>
    appearance === ButtonAppearance.NEGATIVE &&
    `
    padding: 10px 22px;
    color: ${Color.DOLPHIN};
    background-color: ${isDisabled ? Color.MYSTIC : Color.WHITE};
    border: 2px solid transparent;

    ${MediaQuery.M_AND_DOWN} {
      width: 100%;
      display: flex;
      justify-content: center;
    }

    svg {
      color: ${Color.DOLPHIN};
      vertical-align: text-bottom;
      padding-right: ${isIconOnRight ? '0px' : '10px'};
      padding-left : ${isIconOnRight ? '10px' : '0px'};
      
    }

    &:hover {
      color: ${Color.CLOUD_BURST};
      border: 2px solid ${Color.CLOUD_BURST};
    }

    &:focus-visible {
      background-color: ${Color.WHISPER};
      border: 2px solid ${Color.CLOUD_BURST};
    }
    `};

  ${({ appearance, isDisabled, isIconOnRight }) =>
    appearance === ButtonAppearance.OUTLINE &&
    `
    padding: 10px 22px;
    color: ${isDisabled ? Color.DOLPHIN : Color.CLOUD_BURST};
    background-color: ${isDisabled ? Color.MYSTIC : Color.WHITE};
    border: 2px solid ${isDisabled ? 'transparent' : Color.MYSTIC};

    ${MediaQuery.M_AND_DOWN} {
      width: 100%;
      display: flex;
      justify-content: center;
    }

    svg {
      color: ${isDisabled ? Color.DOLPHIN : Color.WHITE};
      vertical-align: text-bottom;
      padding-right: ${isIconOnRight ? '0px' : '10px'};
      padding-left : ${isIconOnRight ? '10px' : '0px'};
      
    }

    &:hover {
      background-color: ${Color.WHITE};
      border: 2px solid ${Color.CLOUD_BURST};
    }

    &:focus-visible {
      background-color: ${Color.WHISPER};
      border: 2px solid ${Color.CLOUD_BURST};
    }
  `};

  ${({ appearance, isDisabled, isIconOnRight, theme }) =>
    appearance === ButtonAppearance.PRIMARY &&
    `
    padding: 10px 22px;
    border: 2px solid transparent;
    background-color: ${
      isDisabled
        ? Color.WHISPER
        : theme === ButtonTheme.DARK
        ? Color.CLOUD_BURST
        : 'none'
    };
    background-image: ${
      isDisabled || theme === ButtonTheme.DARK
        ? 'none'
        : theme === ButtonTheme.WOOFLASH
        ? Gradient.FADE_PURPLE
        : Gradient.FADE_BLUE
    };

    ${MediaQuery.M_AND_DOWN} {
      width: 100%;
      display: flex;
      justify-content: center;
    }

    svg {
      color: ${isDisabled ? Color.DOLPHIN : Color.WHITE};
      vertical-align: text-bottom;
      padding-right: ${isIconOnRight ? '0px' : '10px'};
      padding-left : ${isIconOnRight ? '10px' : '0px'};
      
    }

    &:hover {
      background-image: none;
      background-color: ${Color.WHITE};
      border: 2px solid ${
        theme === ButtonTheme.WOOFLASH
          ? Color.CORNFLOWER_MAUVE_LIGHT
          : theme === ButtonTheme.DARK
          ? Color.CLOUD_BURST
          : Color.BLUE_RIBBON
      };
      color: ${
        theme === ButtonTheme.WOOFLASH
          ? Color.CORNFLOWER_MAUVE_LIGHT
          : theme === ButtonTheme.DARK
          ? Color.CLOUD_BURST
          : Color.BLUE_RIBBON
      };
      svg {
        color: ${
          theme === ButtonTheme.WOOFLASH
            ? Color.CORNFLOWER_MAUVE_LIGHT
            : theme === ButtonTheme.DARK
            ? Color.CLOUD_BURST
            : Color.BLUE_RIBBON
        };
      }
    }
    
    &:focus-visible {
      background-image: none;
      background-color: ${isDisabled ? 'none' : Color.WHISPER};
      border: 2px solid ${Color.CLOUD_BURST};
      color: ${
        theme === ButtonTheme.WOOFLASH
          ? Color.CORNFLOWER_MAUVE_LIGHT
          : theme === ButtonTheme.DARK
          ? Color.CLOUD_BURST
          : Color.BLUE_RIBBON
      };
    }
  `};

  ${({ appearance, isDisabled, isTransparent, iconColor, iconHoverColor }) =>
    appearance === ButtonAppearance.CIRCULAR &&
    `
    justify-content: center;
    padding: none;
    width: 45px;
    min-width: 45px;
    border: 2px solid ${
      // eslint-disable-next-line no-nested-ternary
      isDisabled
        ? 'transparent'
        : isTransparent
        ? 'rgba(255,255,255,0.35)'
        : Color.MYSTIC
    };
    background-image: none;
    background-color: ${
      // eslint-disable-next-line no-nested-ternary
      isDisabled ? Color.WHISPER : isTransparent ? 'transparent' : Color.WHITE
    };
    color: ${
      // eslint-disable-next-line no-nested-ternary
      isDisabled ? Color.DOLPHIN : iconColor || Color.CLOUD_BURST
    };

    &:hover {
      background-color: ${isTransparent ? 'transparent' : Color.WHISPER};
      border: 2px solid ${Color.WHISPER};
      color: ${iconHoverColor || Color.CLOUD_BURST}
    }

    &:focus-visible {
      background-color: ${isDisabled ? 'none' : Color.WHISPER};
      border: 2px solid ${Color.CLOUD_BURST};
    }
  `};

  ${({ appearance, isDisabled, iconHoverColor }) =>
    appearance === ButtonAppearance.CIRCULAR_FLOATING &&
    `
    justify-content: center;
    width: 45px;
    min-width: 45px;
    background-color: ${isDisabled ? Color.MYSTIC : Color.WHITE};
    border: 2px solid ${isDisabled ? Color.MYSTIC : 'transparent'};
    box-shadow: ${Shadow.BLUE_BOX};
    color: ${isDisabled ? Color.DOLPHIN : Color.CLOUD_BURST};

    &:hover {
      border: 2px solid ${Color.WHISPER};
      background-color: ${Color.WHISPER};
      color: ${iconHoverColor || Color.CLOUD_BURST}
    }

    &:focus-visible {
      background-color: ${isDisabled ? 'none' : Color.WHISPER} ;
      border: 2px solid ${Color.CLOUD_BURST};
    }
  `};

  ${({ appearance, isDisabled, isIconOnRight }) =>
    appearance === ButtonAppearance.MINIMAL &&
    `
    color: ${isDisabled ? Color.DOLPHIN : Color.CLOUD_BURST};
    line-height: 22px;
    font-size: ${FontSize.S};
    font-weight: ${FontWeight.BLACK};
    min-height: initial;
    width: fit-content;
    border: none;
    background-color: transparent;
    border: 2px solid transparent;

    &:hover {
      color: ${Color.BLUE_RIBBON};

      svg {
        color: ${isDisabled ? Color.DOLPHIN : Color.BLUE_RIBBON};
      }
    }

    svg {
      color: ${isDisabled ? Color.DOLPHIN : Color.CLOUD_BURST};
      vertical-align: text-bottom;
      padding-right: ${isIconOnRight ? '0px' : '10px'};
      padding-left : ${isIconOnRight ? '10px' : '0px'};
      
    }

    &:focus-visible {
      background-color: ${isDisabled ? 'none' : Color.WHISPER} ;
      border: 2px solid ${Color.CLOUD_BURST};
    }
  `};

  ${({ appearance, isDisabled }) =>
    appearance === ButtonAppearance.MINIMAL &&
    `
    color: ${isDisabled ? Color.DOLPHIN : Color.CLOUD_BURST};
    line-height: 22px;
    font-size: ${FontSize.S};
    font-weight: ${FontWeight.BLACK};
    min-height: initial;
    width: fit-content;
    border: none;
    background-color: transparent;
    border: 2px solid transparent;

    &:hover {
      color: ${Color.BLUE_RIBBON};
    }

    &:focus-visible {
      background-color: ${isDisabled ? 'none' : Color.WHISPER} ;
      border: 2px solid ${Color.CLOUD_BURST};
    }
  `};
`;

export const StyledGatsbyLink = styled(Link)`
  text-decoration: none;
  ${ThemeButton};
`;

export const StyledLink = styled.a`
  text-decoration: none;
  ${ThemeButton};
`;

export const StyledButton = styled.button`
  ${ThemeButton};
`;
